import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Radar, Loader2, AlertTriangle, Copy, Layers, ShieldCheck } from "lucide-react";

const SEV = {
  high: "bg-red-50 text-red-600 border-red-100",
  medium: "bg-amber-50 text-amber-600 border-amber-100",
  low: "bg-slate-50 text-slate-500 border-slate-100",
};

export default function GapRadar() {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    setLoading(true); setData(null);
    try {
      const r = await api.get("/gap-radar");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("gap_failed"));
    } finally { setLoading(false); }
  };

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><Radar className="h-7 w-7 text-brandblue" /> {t("gap_title")}</h1>
      <p className="mt-1 text-slate-500 max-w-2xl">{t("gap_intro")}</p>

      {!data && (
        <div className="mt-8 flex flex-col items-center justify-center rounded-3xl bg-white border border-slate-100 card-shadow p-12 text-center max-w-2xl">
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl brand-gradient text-white"><Radar className="h-8 w-8" /></span>
          <p className="mt-4 text-slate-500 max-w-sm">{t("gap_cta_sub")}</p>
          <Button data-testid="gap-run" onClick={run} disabled={loading} className="mt-5 rounded-full brand-gradient text-white py-6 px-8 font-semibold">
            {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> {t("gap_scanning")}</> : t("gap_run")}
          </Button>
        </div>
      )}

      {data && (
        <div className="mt-6 space-y-6 max-w-4xl" data-testid="gap-result">
          {data.summary && <div className="rounded-2xl bg-blue-50 border border-blue-100 p-5 text-navy">{data.summary}</div>}

          <section>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-navy mb-3"><AlertTriangle className="h-5 w-5 text-red-500" /> {t("coverage_gaps")} ({data.gaps?.length || 0})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="gap-gaps">
              {(data.gaps || []).map((g, i) => (
                <div key={i} className={`rounded-2xl border card-shadow p-5 bg-white`}>
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-semibold text-navy">{g.title}</h3>
                    <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${SEV[g.severity] || SEV.low}`}>{t(`sev_${g.severity}`) || g.severity}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-500">{g.why}</p>
                  {g.suggestion && <p className="mt-2 text-sm font-medium text-brandblue">→ {g.suggestion}</p>}
                </div>
              ))}
              {(!data.gaps || data.gaps.length === 0) && <p className="text-sm text-slate-400">{t("no_gaps")}</p>}
            </div>
          </section>

          <section>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-navy mb-3"><Layers className="h-5 w-5 text-covered" /> {t("overlaps")} ({data.overlaps?.length || 0})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="gap-overlaps">
              {(data.overlaps || []).map((o, i) => (
                <div key={i} className="rounded-2xl border border-emerald-100 bg-emerald-50/40 card-shadow p-5">
                  <h3 className="font-semibold text-navy">{o.title}</h3>
                  <p className="mt-2 text-sm text-slate-500">{o.detail}</p>
                  {o.estimated_saving && <p className="mt-2 text-sm font-semibold text-covered">{t("could_save")}: {o.estimated_saving}</p>}
                </div>
              ))}
              {(!data.overlaps || data.overlaps.length === 0) && <p className="text-sm text-slate-400 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-covered" />{t("no_overlaps")}</p>}
            </div>
          </section>

          <Button variant="outline" onClick={run} disabled={loading} className="rounded-full">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("gap_rescan")}</Button>
        </div>
      )}
    </AppLayout>
  );
}
